import { useMemo } from "react";
import { StackedBarChart } from "./components/StackedBarChart/StackedBarChart";
import { Container } from "~/components/Container";
import { Card } from "~/components/Card";
import { Col, Grid } from "~/components/Grid";
import { H4 } from "~/components/Typography";
import { Flex } from "~/components/Flex";
import { AppBar, AppBarContainer, AppBarHeading } from "~/components/AppBar";
import { Box } from "~/components/Box";
import {
  convertChartData,
  FilterKeyType,
  filters,
  getDateRange,
  getPercentageByItem,
  secondTablebLastPurchase,
  topRoutes,
} from "./utils";
import { CostCenterResume } from "./components/CostCenterResume/CostCenterResume";
import { IssuerResume } from "./components/IssuerResume/IssuerResume";
import { PercentageChart } from "./components/PercentageChart/PercentageChart";
import { ChartByItem } from "./components/ChartByItem/ChartByItem";
import { Skeleton } from "~/components/Skeleton";
import MonthlyChart from "./components/MonthlyChart/MonthlyChart";
import PurchaseTable from "./components/LastPurchase/LastPurchase";
import { getServiceLabel } from "~/core/shared/utils/order.utils";
import { Button } from "~/components/Button";
import { CustomersBiContainerProps } from "./types";
import { Text } from "~/components/Text";
import { displayDate } from "~/utils/date.utils";
import { Tag } from "~/components/Tag";
import { Icon } from "~/components/Icon";
import { SvgClose } from "~/components/Icon/icons";
import { OrderItems } from "~/application/types";
import { ByProjectList } from "./components/ByProjectList/ByProjectList";
import { ByPhaseList } from "./components/ByPhaseList/ByPhaseList";
import { UnitList } from "./components/ByUnit/ByUnit";

export function CustomerBiContaner({
  dashboard,
  onCloseModal,
  isLoading,
  filterData,
  activeFilter,
  onSetActiveValue,
  onRemoveItem,
  onSelectMultiValues,
  onSelectFilterType,
  selectedMultiValues,
  handleSelectStartDate,
  onSelectPeriodDate,
}: CustomersBiContainerProps) {
  const filteredData = useMemo(() => convertChartData(dashboard?.totalSpent ?? []), [dashboard]);
  const periodTypePeriod = activeFilter.type === "threeMonths" ? "month" : "year";

  const filterKeyValue = activeFilter.type === "three-months" ? "threeMonths" : activeFilter.type;
  const isSingleDayPeriod = useMemo(() => {
    const { startDate, endDate } = selectedMultiValues.filterRangePeriod || {};
    if (startDate && endDate) {
      const normalizeDate = (inputDate: string) => {
        const d = new Date(inputDate);
        d.setHours(0, 0, 0, 0);
        return d;
      };
      const normalizedStartDate = normalizeDate(startDate);
      const normalizedEndDate = normalizeDate(endDate);
      return normalizedStartDate.getTime() === normalizedEndDate.getTime();
    }
    return false;
  }, [selectedMultiValues.startEndDate]);

  const dataItem =
    dashboard?.dataByItem?.map(({ item, value }) => ({
      item: item,
      name: getServiceLabel(item || ""),
      value,
    })) || [];

  const roadValue = dataItem.filter((item) => item.item === OrderItems.ROAD).at(0)?.value || 0;
  const airwayValue = dataItem.filter((item) => item.item === OrderItems.AIRWAY).at(0)?.value || 0;
  const vehicleValue =
    dataItem.filter((item) => item.item === OrderItems.VEHICLE).at(0)?.value || 0;
  const hotelValue = dataItem.filter((item) => item.item === OrderItems.HOTEL).at(0)?.value || 0;

  const totalValue = dataItem.reduce((acc, item) => item.value + acc, 0);
  const percentageByItem = getPercentageByItem({
    roadValue,
    airwayValue,
    hotelValue,
    totalValue,
    vehicleValue,
  });
  return (
    <Flex direction="column">
      <AppBar>
        <AppBarContainer>
          <AppBarHeading title="Dashboard BI" />
        </AppBarContainer>
      </AppBar>
      <Container css={{ py: "$10", "@mxlg": { p: "$5" }, "@lg": { maxWidth: "100vw" } }}>
        {/* TODO - Modal de filtro oculto para refatorações futuras */}
        <>
          {!isLoading.totalSpent ? (
            <Card css={{ overflow: "visible" }}>
              <Col css={{ width: "95%", margin: "0 auto", py: "$10" }}>
                <Flex align="center" justify="between">
                  <H4>Últimos gastos totais</H4>
                  {selectedMultiValues.filterRangePeriod?.startDate &&
                    selectedMultiValues.filterRangePeriod?.endDate &&
                    selectedMultiValues.filterRangePeriod.isPeriodOnBar && (
                      <Tag>
                        <Flex>
                          <Text>
                            {isSingleDayPeriod
                              ? `Data selecionada: ${displayDate(
                                  selectedMultiValues.filterRangePeriod?.startDate ?? new Date()
                                )}`
                              : `Data selecionada: ${displayDate(
                                  selectedMultiValues.filterRangePeriod?.startDate ?? new Date()
                                )} até ${displayDate(
                                  selectedMultiValues.filterRangePeriod?.endDate ?? new Date()
                                )}`}
                          </Text>
                        </Flex>
                        <Flex>
                          <Button
                            onClick={() =>
                              onSelectPeriodDate({
                                startDate: getDateRange(periodTypePeriod).startDate,
                                endDate: getDateRange(periodTypePeriod).endDate,
                                isPeriodOnBar: false,
                              })
                            }
                            size="sm"
                            css={{ p: "2px", height: "20px" }}
                          >
                            <Icon as={SvgClose} />
                          </Button>
                        </Flex>
                      </Tag>
                    )}
                </Flex>
                <Flex gap="3">
                  {filters.map((filter, index) => (
                    <Box
                      css={{
                        mt: "$5",
                        p: "$3",
                        cursor: "pointer",
                        backgroundColor:
                          activeFilter.type === filter.type ? "$primary-base" : "transparent",
                        color: activeFilter.type === filter.type ? "#fff" : "inherit",
                        borderRadius: "$md",
                      }}
                      key={`customer-bi-${index}`}
                      onClick={() => {
                        onSelectFilterType(
                          filter.type === FilterKeyType.THREE_MONTHS ? "three-months" : filter.type
                        );

                        onSetActiveValue({ type: filter?.type, value: filter.value });

                        onSelectPeriodDate(null);

                        onSelectPeriodDate({
                          startDate: filter.startDate,
                          endDate: filter.endDate,
                          isPeriodOnBar: false,
                        });
                      }}
                    >
                      {filter.value}
                    </Box>
                  ))}
                </Flex>
              </Col>
              <Flex
                css={{
                  width: "100%",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    overflowX: "auto",
                  },
                  "@media (pointer: coarse)": {
                    overflowX: "auto",
                    paddingBottom: "4px",
                    "&::-webkit-scrollbar": {
                      height: "6px",
                    },
                  },
                }}
              >
                <Box
                  css={{
                    width: "100%",
                    minWidth: "max-content",
                    "@sm": { minWidth: "100%" },
                    "@md": { minWidth: 1750 },
                  }}
                >
                  <StackedBarChart
                    chartData={filteredData[filterKeyValue as keyof typeof filteredData]}
                    filterKey={filterKeyValue}
                    onSetActiveValue={onSetActiveValue}
                    handleSelectStartDate={handleSelectStartDate}
                    onSelectPeriodDate={onSelectPeriodDate}
                    selectedMultiValues={selectedMultiValues}
                    onSelectMultiValues={onSelectMultiValues}
                  />
                </Box>
              </Flex>
            </Card>
          ) : (
            <Skeleton css={{ width: "100%", height: "400px", margin: "0 auto" }} />
          )}

          {(dashboard?.byPhase?.length ?? 0) > 0 ||
          (dashboard?.byProjects?.length ?? 0) > 0 ||
          (dashboard?.byUnit?.length ?? 0) > 0 ? (
            <Grid columns={3} gap={4} css={{ mt: "$4" }}>
              {!isLoading.byUnit ? (
                <UnitList
                  unitSummary={dashboard?.byUnit}
                  onSelectMultiValues={onSelectMultiValues}
                  selectedMultiValues={selectedMultiValues}
                />
              ) : (
                <Skeleton css={{ width: "100%", height: "600px", margin: "0 auto" }} />
              )}

              {!isLoading.isLoadingProjects ? (
                <ByProjectList
                  projects={dashboard?.byProjects}
                  selectedMultiValues={selectedMultiValues}
                  onSelectMultiValues={onSelectMultiValues}
                />
              ) : (
                <Skeleton css={{ width: "100%", height: "600px", margin: "0 auto" }} />
              )}

              {!isLoading.isFetchingByPhases ? (
                <ByPhaseList
                  phases={dashboard?.byPhase}
                  selectedMultiValues={selectedMultiValues}
                  onSelectMultiValues={onSelectMultiValues}
                />
              ) : (
                <Skeleton css={{ width: "100%", height: "600px", margin: "0 auto" }} />
              )}
            </Grid>
          ) : null}

          <Grid gap="4" columns={{ "@initial": 3, "@mxlg": 1 }} css={{ height: "auto", py: "$5" }}>
            <>
              {!isLoading.costCenter ? (
                <CostCenterResume
                  costCenterSummary={dashboard?.byCostCenter}
                  selectedMultiValues={selectedMultiValues}
                  onSelectMultiValues={onSelectMultiValues}
                />
              ) : (
                <Skeleton css={{ width: "100%", height: "600px", margin: "0 auto" }} />
              )}
              {!isLoading.byIssuer ? (
                <IssuerResume
                  issuerResume={dashboard?.byIssuer}
                  selectedMultiValues={selectedMultiValues}
                  onSelectMultiValues={onSelectMultiValues}
                />
              ) : (
                <Skeleton css={{ width: "100%", height: "600px", margin: "0 auto" }} />
              )}

              {!isLoading.dataByItem ? (
                <Card
                  css={{
                    overflowY: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Flex direction="column" justify="between" css={{ p: "$5" }}>
                    <H4>Porcentagem por produto</H4>
                    {selectedMultiValues.items.length ? (
                      <Flex css={{ pt: "$3" }}>
                        <Tag variant="neutral">
                          <Text>Item selecionado</Text>
                          <Flex wrap="wrap">
                            {selectedMultiValues.items.map((item, index) => (
                              <Button
                                key={`selected-value-${item}`}
                                onClick={() => onRemoveItem(item)}
                                size="sm"
                                css={{ p: "$3", height: "20px", margin: "2px" }}
                              >
                                {getServiceLabel(item)} <Icon as={SvgClose} />
                              </Button>
                            ))}
                          </Flex>
                        </Tag>
                      </Flex>
                    ) : null}
                  </Flex>
                  <Flex align="end" justify="center">
                    <PercentageChart
                      size="xlarge"
                      percents={percentageByItem}
                      selectedMultiValues={selectedMultiValues}
                      onSelectMultiValues={onSelectMultiValues}
                    />
                  </Flex>
                </Card>
              ) : (
                <Skeleton css={{ width: "100%", height: "600px", margin: "0 auto" }} />
              )}
            </>
          </Grid>

          <Grid columns={2} gap="4">
            {!isLoading.dataByItem ? (
              <Grid
                gap="4"
                columns={{
                  "@initial":
                    !dashboard?.dataByItem?.length || dashboard?.dataByItem?.length === 1 ? 1 : 2,
                  "@mxlg": 1,
                }}
              >
                <ChartByItem
                  chartByItem={dashboard?.dataByItem}
                  percentageByItem={dashboard?.percentageByItem}
                />
              </Grid>
            ) : (
              <Grid columns={2} gap="2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton
                    key={`skeleton-${index}`}
                    css={{ width: "100%", height: "250px", margin: "0 auto" }}
                  />
                ))}
              </Grid>
            )}

            {!isLoading.leadTime ? (
              <Card>
                <MonthlyChart data={dashboard?.leadTime} selectedFilter={selectedMultiValues} />
              </Card>
            ) : (
              <Skeleton css={{ width: "100%", height: "510px", margin: "0 auto" }} />
            )}
          </Grid>
          <Grid gap="5" columns={2} css={{ py: "$5" }}>
            {!isLoading.topRoutes ? (
              <PurchaseTable
                columnContent={dashboard?.topRoutes}
                columData={topRoutes}
                columns={4}
                title="Top Rotas"
              />
            ) : (
              <Skeleton css={{ width: "100%", height: "400px", margin: "0 auto" }} />
            )}

            {!isLoading.routeAnalysis ? (
              <PurchaseTable
                columnContent={dashboard?.routeAnalysis}
                columData={secondTablebLastPurchase}
                columns={5}
                title="Análise de rota"
              />
            ) : (
              <Skeleton css={{ width: "100%", height: "400px", margin: "0 auto" }} />
            )}
          </Grid>
        </>
      </Container>
    </Flex>
  );
}
